import style from "./License.module.scss";
import {
  useActivateLicenseMutation,
  useGetTokenQuery,
} from "@/core/api/license/license";
import { snackbarFail, snackbarSuccess } from "@/services/snackbar.service";
import { Button, FileUploader, useSnackbars } from "@aurora/components";
import cx from "classnames";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface IProps {
  callback?: (value: any) => void;
}

const LicenseRenewal: FC<IProps> = ({ callback }) => {
  const { snackbarInfo } = useSnackbars();

  const [isDownloading, setDownloading] = useState(false);
  const [fileUploader, setFileUploader] = useState<any>([]);
  const [license, setLicense] = useState<string | null>(null);
  const navigate = useNavigate();

  const { data: token, error: isError } = useGetTokenQuery({});
  const [activateLicense] = useActivateLicenseMutation();

  useEffect(() => {
    if (isError && "status" in isError) {
      const fail: any = snackbarFail(
        "Недостаточно прав на выполнение операции",
      );
      snackbarInfo?.show(fail);
    }
  }, [isError]);

  const downloadKey = async () => {
    setDownloading(true);
    if (token) {
      // искусственная задержка в 1.5 секунды
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Используем data URL напрямую
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;txt,${token.data}`;
      link.download = "data-license";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setDownloading(false);
  };

  const handleFileChange = (files: any) => {
    const file = files?.[0];
    setFileUploader(files);
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      setLicense(reader.result as string);
    };

    reader.onerror = () => {
      const fail: any = snackbarFail("Ошибка при чтении файла");
      snackbarInfo?.show(fail);
    };

    reader.readAsText(file); // Читаем файл как текст
  };

  useEffect(() => {
    if (!license || license.length === 0) return;

    const activate = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        await activateLicense({ license }).unwrap();
        snackbarInfo?.show(snackbarSuccess("Лицензия успешно обновлена"));
        // разлогин после успешного применения лицензии
        setTimeout(async () => navigate("/logout"), 1000);
      } catch (err) {
        console.error("Ошибка активации лицензии:", err);
        const fail: any = snackbarFail(
          "Невозможно продлить лицензию. Обратитесь в отдел продаж sales@fofk-tech.ru",
        );
        snackbarInfo?.show(fail);
      } finally {
        // сбрасываем значения
        setFileUploader([]);
        setLicense(null);

        setTimeout(() => callback && callback(false), 500);
      }
    };

    activate();
  }, [license, activateLicense]);

  return (
    <>
      <h1>Обновление лицензии</h1>
      <p className={"mb-20"}>
        Для того, чтобы обновить лицензию, выполните следующие шаги по
        инструкции.
      </p>
      <div className={cx(style.LicenseRenewal, "d-flex flex-column")}>
        <p>1. Скачайте архив для создания лицензии</p>
        <Button
          disabled={isDownloading}
          loading={isDownloading}
          onClick={downloadKey}
          shape={"inline"}
          size={"m"}
          variant={"primary"}
        >
          Скачать
        </Button>
      </div>

      <div className={cx(style.LicenseRenewal, "d-flex flex-column")}>
        <p>2. Отправьте архив в отдел продаж на почту:</p>
        <a href="mailto://sales@fork-tech.ru">sales@fork-tech.ru</a>
      </div>
      <div className={cx(style.LicenseRenewal, "d-flex flex-column")}>
        <p>3. Загрузите полученный файл от менеджера</p>
        <FileUploader
          caption={""}
          className={"px-0"}
          files={fileUploader}
          filesLimit={1}
          listShape="cardCell"
          mode="attach"
          onChange={handleFileChange}
          title={"Загрузите"}
          validateFiles={async files => {
            const _5KB = 5120;
            const fiteredFiles = files.filter(f => f.size <= _5KB);

            if (!fiteredFiles.length) {
              alert("Файл не должен превышать 5КБ");
            }

            return fiteredFiles;
          }}
          variant="icon"
        />
        {fileUploader.length > 0 && (
          <Button disabled loading shape="inline">
            Проверка лицензии
          </Button>
        )}
      </div>
    </>
  );
};

export default LicenseRenewal;
